import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { Briefcase, FileText, Calendar, CheckCircle, UserCheck, DollarSign, TrendingUp, Download, Cake, Heart, Award, Target as TargetIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AnnouncementBanner } from './Announcements';
import { TalentBuzzWidget } from './TalentBuzz';
import DateRangeFilter from '../components/DateRangeFilter';
import OnboardingChecklist from '../components/OnboardingChecklist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MetricCard = ({ icon: Icon, label, value, color, colorClass }) => (
  <div 
    data-testid={`metric-card-${label.toLowerCase().replace(/\s/g, '-')}`}
    className={`metric-card ${colorClass} bg-white border border-slate-100 p-3 sm:p-6 hover:shadow-lg transition-shadow duration-300 relative overflow-hidden`}
  >
    <div className="flex items-start justify-between mb-4">
      <div className="p-3 rounded-sm" style={{ backgroundColor: color + '15' }}>
        <Icon size={24} style={{ color }} />
      </div>
    </div>
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 sm:mb-2">{label}</p>
      <p className="text-xl sm:text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color }}>
        {typeof value === 'number' && value % 1 !== 0 ? value.toFixed(2) : value}
      </p>
    </div>
  </div>
);

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [user, setUser] = useState(null);
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [targetsDraft, setTargetsDraft] = useState({ interview_target: 33, closure_target: 9, joining_target: 100 });
  const [savingTargets, setSavingTargets] = useState(false);
  const [hitRates, setHitRates] = useState(null);
  const [celebrations, setCelebrations] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const fetchMetrics = async (filter, from, to) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/dashboard/metrics?time_filter=${filter}`;
      if (from) url += `&date_from=${from}`;
      if (to) url += `&date_to=${to}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      toast.error('Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingInterviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/interviews/upcoming?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpcomingInterviews(response.data);
    } catch {
      /* silent */
    }
  };

  const fetchHitRates = async (filter, from, to) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/dashboard/hit-rates?time_filter=${filter}`;
      if (from) url += `&date_from=${from}`;
      if (to) url += `&date_to=${to}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHitRates(response.data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    fetchMetrics(timeFilter, dateFrom, dateTo);
    fetchHitRates(timeFilter, dateFrom, dateTo);
    fetchUpcomingInterviews();
    fetchCelebrations();
  }, [timeFilter]);

  const fetchCelebrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/celebrations/today`, { headers: { Authorization: `Bearer ${token}` } });
      setCelebrations(res.data);
    } catch (err) { console.error("./pages/Dashboard.jsx:", err); }
  };

  const handleExport = () => {
    toast.success('Export feature coming soon');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-slate-500">Loading dashboard...</p>
      </div>
    );
  }

  const chartData = metrics ? [
    { name: 'Positions', value: metrics.total_positions, fill: '#4F46E5' },
    { name: 'Resumes', value: metrics.total_resumes, fill: '#EC4899' },
    { name: 'Interviews', value: metrics.total_interviews, fill: '#F59E0B' },
    { name: 'Closures', value: metrics.total_closures, fill: '#10B981' },
    { name: 'Joinings', value: metrics.total_joinings, fill: '#06B6D4' },
  ] : [];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const greetingMessages = [
    "Let's make today count!",
    "Ready to find the perfect candidate?",
    "Every great company starts with great hiring.",
    "Your next big placement could be today!",
    "Small steps every day lead to big results.",
    "Let's close some positions today!",
    "Keep pushing — your hard work is paying off.",
    "Time to connect talent with opportunity!",
  ];
  const greetMsg = greetingMessages[new Date().getDate() % greetingMessages.length];

  return (
    <div>
      <AnnouncementBanner />

      {/* Personalized Greeting */}
      {user && (
        <div className="mb-6 p-5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl text-white" data-testid="dashboard-greeting">
          <h2 className="text-2xl font-extrabold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="greeting-text">
            {getGreeting()}, {user.name?.split(' ')[0]}!
          </h2>
          <p className="text-blue-100 text-sm mt-1" data-testid="greeting-message">{greetMsg}</p>
        </div>
      )}

      {/* Celebrations Widget */}
      {celebrations && (celebrations.birthdays?.length > 0 || celebrations.marriage_anniversaries?.length > 0 || celebrations.work_anniversaries?.length > 0) && (
        <div className="mb-6 bg-white border border-amber-200 rounded-xl p-4 shadow-sm" data-testid="celebrations-widget">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-3 flex items-center gap-1.5">
            <Cake size={14} /> Today's Celebrations
          </p>
          <div className="flex flex-wrap gap-3">
            {celebrations.birthdays?.map((b) => (
              <div key={`bd-${b.name}`} data-testid={`birthday-${b.name}`} className="flex items-center gap-2 bg-pink-50 border border-pink-200 rounded-lg px-3 py-2">
                <Cake size={16} className="text-pink-500" />
                <div><p className="text-sm font-bold text-pink-900">{b.name}</p><p className="text-[10px] text-pink-600 font-semibold uppercase">Birthday</p></div>
              </div>
            ))}
            {celebrations.marriage_anniversaries?.map((m) => (
              <div key={`ma-${m.name}`} data-testid={`marriage-anniv-${m.name}`} className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                <Heart size={16} className="text-rose-500" />
                <div><p className="text-sm font-bold text-rose-900">{m.name}</p><p className="text-[10px] text-rose-600 font-semibold uppercase">Marriage Anniversary</p></div>
              </div>
            ))}
            {celebrations.work_anniversaries?.map((w) => (
              <div key={`wa-${w.name}`} data-testid={`work-anniv-${w.name}`} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <Award size={16} className="text-emerald-500" />
                <div><p className="text-sm font-bold text-emerald-900">{w.name}</p><p className="text-[10px] text-emerald-600 font-semibold uppercase">{w.years} Year{w.years > 1 ? 's' : ''} Work Anniversary</p></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <OnboardingChecklist />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 mb-1 sm:mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Dashboard</h1>
          <p className="text-sm sm:text-base text-slate-600">Performance metrics and insights</p>
        </div>
        <div className="flex gap-3 mt-4 md:mt-0 items-center flex-wrap">
          <DateRangeFilter
            timeFilter={timeFilter}
            setTimeFilter={(v) => { setTimeFilter(v); }}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            onApplyRange={() => { fetchMetrics('custom', dateFrom, dateTo); fetchHitRates('custom', dateFrom, dateTo); }}
          />
          <Button 
            onClick={handleExport} 
            data-testid="export-button"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold active:scale-95 transition-transform"
          >
            <Download size={16} className="mr-2" />
            Export
          </Button>
        </div>
      </div>

      {metrics && (
        <>
          <div className="bento-grid mb-8">
            <MetricCard
              icon={Briefcase}
              label="Total Positions"
              value={metrics.total_positions}
              color="#4F46E5"
              colorClass="metric-positions"
            />
            <MetricCard
              icon={FileText}
              label="Resumes Sent"
              value={metrics.total_resumes}
              color="#EC4899"
              colorClass="metric-resumes"
            />
            <MetricCard
              icon={Calendar}
              label="Interviews"
              value={metrics.total_interviews}
              color="#F59E0B"
              colorClass="metric-interviews"
            />
            <MetricCard
              icon={CheckCircle}
              label="Closures"
              value={metrics.total_closures}
              color="#10B981"
              colorClass="metric-closures"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-8">
            <MetricCard
              icon={UserCheck}
              label="Joinings"
              value={metrics.total_joinings}
              color="#06B6D4"
              colorClass="metric-joinings"
            />
            {user && user.role === 'admin' && (
              <MetricCard
                icon={DollarSign}
                label="Total Revenue"
                value={`₹${metrics.total_revenue.toLocaleString('en-IN')}`}
                color="#0F172A"
                colorClass="metric-revenue"
              />
            )}
            <MetricCard
              icon={TrendingUp}
              label="Joinings / Recruiter"
              value={metrics.joinings_per_recruiter.toFixed(1)}
              color="#8B5CF6"
              colorClass="metric-ask-rate"
            />
          </div>

          <div className="bg-white border border-slate-200 p-3 sm:p-6 mb-8">
            <h2 className="text-lg sm:text-2xl font-bold tracking-tight text-slate-900 mb-4 sm:mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Recruitment Funnel</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" style={{ fontSize: '12px', fontWeight: 500 }} />
                <YAxis stroke="#64748B" style={{ fontSize: '12px', fontWeight: 500 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '2px',
                    fontWeight: 600
                  }} 
                />
                <Bar dataKey="value" radius={[0, 0, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {hitRates && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-blue-900">Hit Rate Targets</h3>
                {user && user.role === 'admin' && (
                  <Button
                    data-testid="edit-hit-rate-targets-btn"
                    variant="outline"
                    size="sm"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={() => {
                      setTargetsDraft({
                        interview_target: hitRates.interview_target,
                        closure_target: hitRates.closure_target,
                        joining_target: hitRates.joining_target,
                      });
                      setTargetsOpen(true);
                    }}
                  >
                    <TargetIcon size={14} className="mr-2" /> Edit Targets
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[
                { title: 'Interview Hit Rate', subtitle: 'Interviews / Resumes Sent', rate: hitRates.interview_hit_rate, target: hitRates.interview_target, color: '#F59E0B', bgFrom: 'from-amber-50', border: 'border-amber-200', nums: `${hitRates.interviews} / ${hitRates.resumes}` },
                { title: 'Closure Hit Rate', subtitle: 'Closures / Interviews', rate: hitRates.closure_hit_rate, target: hitRates.closure_target, color: '#10B981', bgFrom: 'from-emerald-50', border: 'border-emerald-200', nums: `${hitRates.closures} / ${hitRates.interviews}` },
                { title: 'Joining Hit Rate', subtitle: 'Joinings / Closures', rate: hitRates.joining_hit_rate, target: hitRates.joining_target, color: '#06B6D4', bgFrom: 'from-cyan-50', border: 'border-cyan-200', nums: `${hitRates.joinings} / ${hitRates.closures}` },
              ].map((item) => (
                <div key={item.title} className={`bg-gradient-to-br ${item.bgFrom} to-white border ${item.border} rounded p-5`} data-testid={`hit-rate-${item.title.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: item.color }}>{item.title}</p>
                    <span className="text-xs text-slate-500">{item.nums}</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">{item.subtitle}</p>
                  <div className="relative h-6 bg-slate-100 rounded overflow-hidden mb-2">
                    <div className="h-full rounded transition-all duration-500" style={{ width: `${Math.min(item.rate, 100)}%`, backgroundColor: item.color }} />
                    <div className="absolute top-0 h-full w-0.5 bg-red-500" style={{ left: `${Math.min(item.target, 100)}%` }} title={`Target: ${item.target}%`} />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-extrabold" style={{ color: item.color, fontFamily: 'Manrope, sans-serif' }}>{item.rate}%</span>
                    <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded">Target: {item.target}%</span>
                  </div>
                </div>
              ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-blue-100 rounded shadow-sm p-3 sm:p-6 mb-8" data-testid="dashboard-interviews-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-2xl font-bold tracking-tight text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Interviews <span className="text-sm font-normal text-slate-500 ml-2">({upcomingInterviews.length} visible)</span>
              </h2>
              <a href="/interviews" className="text-sm text-blue-700 hover:underline font-semibold">View all →</a>
            </div>
            {upcomingInterviews && upcomingInterviews.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcomingInterviews.slice(0, 12).map((interview) => {
                  const STATUS_COLORS = {
                    scheduled: 'bg-blue-100 text-blue-800',
                    rescheduled: 'bg-amber-100 text-amber-800',
                    completed: 'bg-emerald-100 text-emerald-800',
                    selected: 'bg-emerald-100 text-emerald-800',
                    no_show: 'bg-rose-100 text-rose-800',
                    rejected: 'bg-rose-100 text-rose-800',
                  };
                  const statusClass = STATUS_COLORS[interview.status] || 'bg-slate-100 text-slate-700';
                  const today = new Date(); today.setHours(0,0,0,0);
                  const iDate = interview.date ? new Date(interview.date) : null;
                  const isUpcoming = iDate && iDate >= today;
                  return (
                    <div key={interview.id}
                         className={`p-4 border rounded hover:shadow-md transition-shadow ${isUpcoming ? 'bg-gradient-to-br from-blue-50 to-white border-blue-200' : 'bg-slate-50 border-slate-200'}`}
                         data-testid={`interview-card-${interview.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-blue-900 truncate">{interview.candidate_name}</p>
                          <p className="text-sm text-slate-600 truncate">{interview.position_title}</p>
                        </div>
                        <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded whitespace-nowrap ${statusClass}`}>
                          {interview.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {interview.type && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded bg-indigo-100 text-indigo-800">{interview.type}</span>
                        )}
                        {interview.round && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded bg-violet-100 text-violet-800">{interview.round}</span>
                        )}
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-blue-100">
                        <span className="text-xs text-slate-500 truncate">{interview.recruiter_name}</span>
                        <span className={`text-xs font-bold whitespace-nowrap ${isUpcoming ? 'text-blue-700' : 'text-slate-500'}`}>
                          {iDate ? iDate.toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar size={48} className="mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No interviews yet</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
            <div className="bg-white border border-slate-200 p-3 sm:p-6">
              <h2 className="text-lg sm:text-2xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Performance Overview</h2>
              <p className="text-slate-600 mb-4">Joinings per recruiter: <span className="font-bold text-slate-900">{metrics.joinings_per_recruiter.toFixed(2)}</span></p>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 border-l-4 border-indigo-600">
                  <span className="text-sm font-medium text-slate-700">Conversion Rate (Positions → Closures)</span>
                  <span className="text-lg font-bold text-slate-900">
                    {metrics.total_positions > 0 ? ((metrics.total_closures / metrics.total_positions) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 border-l-4 border-pink-600">
                  <span className="text-sm font-medium text-slate-700">Interview Success Rate</span>
                  <span className="text-lg font-bold text-slate-900">
                    {metrics.total_interviews > 0 ? ((metrics.total_closures / metrics.total_interviews) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 border-l-4 border-emerald-600">
                  <span className="text-sm font-medium text-slate-700">Joining Confirmation Rate</span>
                  <span className="text-lg font-bold text-slate-900">
                    {metrics.total_closures > 0 ? ((metrics.total_joinings / metrics.total_closures) * 100).toFixed(1) : 0}%
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Upcoming Joinings</h2>
              {metrics.upcoming_joinings && metrics.upcoming_joinings.length > 0 ? (
                <div className="space-y-3">
                  {metrics.upcoming_joinings.map((joining) => (
                    <div key={`${joining.candidate_name}-${joining.joining_date}`} className="p-3 bg-slate-50 border-l-4 border-cyan-600">
                      <p className="font-semibold text-slate-900">{joining.candidate_name}</p>
                      <p className="text-sm text-slate-600">{joining.position_title}</p>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-slate-500">{joining.recruiter_name}</span>
                        <span className="text-xs font-bold text-cyan-700">{new Date(joining.joining_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No upcoming joinings scheduled</p>
              )}
            </div>
          </div>

          {/* Talent Buzz Widget */}
          <div className="mt-6">
            <TalentBuzzWidget />
          </div>
        </>
      )}

      {/* Edit Hit-Rate Targets — admin only */}
      <Dialog open={targetsOpen} onOpenChange={setTargetsOpen}>
        <DialogContent className="rounded max-w-md" data-testid="edit-hit-rate-targets-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900">Edit Hit Rate Targets</DialogTitle>
            <DialogDescription>
              Set the targets your team should aim for on each funnel stage. Saved for your organization only; recruiters see the same targets on their dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-amber-700">Interview Hit Rate Target (%)</Label>
              <p className="text-xs text-slate-500 mb-1">Interviews per 100 resumes sent</p>
              <Input
                type="number" min={0} max={100}
                value={targetsDraft.interview_target}
                onChange={(e) => setTargetsDraft(p => ({ ...p, interview_target: e.target.value }))}
                data-testid="hit-rate-interview-target"
                className="mt-1 rounded border-amber-200"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-emerald-700">Closure Hit Rate Target (%)</Label>
              <p className="text-xs text-slate-500 mb-1">Closures per 100 interviews</p>
              <Input
                type="number" min={0} max={100}
                value={targetsDraft.closure_target}
                onChange={(e) => setTargetsDraft(p => ({ ...p, closure_target: e.target.value }))}
                data-testid="hit-rate-closure-target"
                className="mt-1 rounded border-emerald-200"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-cyan-700">Joining Hit Rate Target (%)</Label>
              <p className="text-xs text-slate-500 mb-1">Joinings per 100 closures</p>
              <Input
                type="number" min={0} max={100}
                value={targetsDraft.joining_target}
                onChange={(e) => setTargetsDraft(p => ({ ...p, joining_target: e.target.value }))}
                data-testid="hit-rate-joining-target"
                className="mt-1 rounded border-cyan-200"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setTargetsOpen(false)} disabled={savingTargets}>Cancel</Button>
            <Button
              data-testid="save-hit-rate-targets-btn"
              className="bg-blue-700 hover:bg-blue-800 text-white"
              disabled={savingTargets}
              onClick={async () => {
                setSavingTargets(true);
                try {
                  const token = localStorage.getItem('token');
                  await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/dashboard/hit-rate-targets`,
                    targetsDraft,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  toast.success('Hit-rate targets saved');
                  setTargetsOpen(false);
                  // Refresh hit rates so the new targets render immediately
                  const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/hit-rates`, { headers: { Authorization: `Bearer ${token}` } });
                  setHitRates(res.data);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to save targets');
                } finally {
                  setSavingTargets(false);
                }
              }}
            >{savingTargets ? 'Saving…' : 'Save Targets'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
